import { IWorkspaceAction } from '../../@types/workspaceTypes';
import { Dispatch, ReactElement, SetStateAction, useContext, useState } from 'react';
import useEuSalesModal from '../../components/modals/ECSalesModal';
import {
  IFinancialPeriod,
  IGenerateEcSalesListingMutationResult,
  IGenerateEcSalesListingMutationVariables,
} from '../../graphql/schema/p4types';
import { IJobMeta, useBackgroundMutation } from '../../utils/BackgroundJobs';
import WorkspaceContext from '../../components/Context/WorkspaceContext';
import generateEcSalesListing from '../../graphql/mutations/customer/GenerateEcSalesListingMutation.graphql';
import getClientMutationId from '../../utils/getClientMutationId';
import normalizeVariables from '../../graphql/schema/normalizeVariables';
import { client } from '../../client';
import ecSalesListingByUserQuery from '../../graphql/queries/vat/EcSalesListingByUserQuery.graphql';
import { Maybe } from 'graphql/jsutils/Maybe';

enum EcSalesWorkspaceActions {
  Generate = 'GENERATE_EU_SALES_REPORT',
  Download = 'PRINT_EU_SALES_REPORT',
}

const useEcSalesWorkspaceActions = (
  fromFinancialPeriod: Maybe<IFinancialPeriod>,
  toFinancialPeriod: Maybe<IFinancialPeriod>,
  setJobId: Dispatch<SetStateAction<string>>
): [generateAction: IWorkspaceAction, actions: Array<IWorkspaceAction>, modals: ReactElement] => {
  const [modalOpen, setModalOpen] = useState(false);
  const { reloadData } = useContext(WorkspaceContext);
  const variables = {
    input: {
      clientMutationId: getClientMutationId(),
      fromFinancialPeriod: fromFinancialPeriod,
      toFinancialPeriod: toFinancialPeriod,
    },
  };

  const [runBackgroundProcess] = useBackgroundMutation<
    IGenerateEcSalesListingMutationResult,
    IGenerateEcSalesListingMutationVariables
  >(generateEcSalesListing, {
    jobTitle: 'Generating EC Sales Listing',
    variables: normalizeVariables(variables),
    onCompleteJob: (job: IJobMeta) => {
      client.refetchQueries({
        include: [ecSalesListingByUserQuery],
      });
      setJobId(job?.id);
      reloadData?.();
    },
  });

  const generate = () => {
    runBackgroundProcess();
  };

  const modal = useEuSalesModal({
    onClose: () => setModalOpen(false),
    modalOpen: modalOpen,
  });

  const reportActions: Array<IWorkspaceAction> = [
    {
      actionReference: EcSalesWorkspaceActions.Download,
      key: 'eu_sales_report_download',
      text: 'Download',
      onClick: () => {
        setModalOpen(true);
      },
    },
  ];

  const generateAction: IWorkspaceAction<EcSalesWorkspaceActions> = {
    actionReference: EcSalesWorkspaceActions.Generate,
    ignoreBulkActionCheck: true,
    key: 'eu_sales_report_generate',
    text: 'Generate',
    onClick: () => {
      generate();
    },
  };

  return [generateAction, reportActions, modal];
};

export default useEcSalesWorkspaceActions;
